import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import cx from 'classnames'
import ShowResults from 'conversations/components/ConversationSearchResults'
import { Portal } from 'conversations/components/Portal'
import useOnClickOutside from 'conversations/hooks/useOnClickOutside'
import { Subject } from 'rxjs'
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators'
import { localStore } from 'shared/utils/storage'

import { A, Colorway, H4, H6, Input, P } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import LoadingState, { Size, Theme } from '../LoadingState'

import { InsightsConversation, performInsightsConversationSearch } from './api'

import styles from './styles.module.css'
import sharedStyles from 'conversations/css/shared.module.css'

interface RecentSearchValue {
  value: string
  timestamp: number
}

interface SearchModalRowProps {
  hovered: boolean
  onHover: (idx: number) => void
  onSelect: (idx: number) => void
  index: number
  className?: string
}

export const SearchModalRow: React.FC<SearchModalRowProps> = React.memo(
  ({ hovered, onHover, index, onSelect, children, className }): JSX.Element => {
    const ref = useRef<HTMLDivElement>(null)

    const onHoverCallback = useCallback(() => {
      onHover(index)
    }, [index, onHover])

    const onSelectCallback = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        onSelect(index)
      },
      [index, onSelect]
    )

    useLayoutEffect(() => {
      if (ref.current && hovered) {
        ref.current.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
        })
      }
    }, [hovered])

    return (
      <div
        className={cx(className, {
          [styles.itemHovered]: hovered,
        })}
        onMouseOver={onHoverCallback}
        onClick={onSelectCallback}>
        {children}
      </div>
    )
  }
)

SearchModalRow.displayName = 'SearchModalRow'

interface RecentSearchesProps {
  searches: RecentSearchValue[]
  setSearch: (search: string) => void
  hoveredIndex: number | null
  setHoveredIndex: (index: number) => void
}

const RecentSearches: React.FC<RecentSearchesProps> = ({
  searches,
  setSearch,
  hoveredIndex,
  setHoveredIndex,
}): JSX.Element => {
  return (
    <div data-ocweb-id="ShowRecentSearches">
      {searches.map((searchValue, index) => (
        <SearchModalRow
          key={searchValue.timestamp}
          hovered={hoveredIndex === index}
          onHover={setHoveredIndex}
          onSelect={() => setSearch(searchValue.value)}
          index={index}
          className={styles.recentSearchItem}>
          <Icon className={styles.searchIcon} width={13} height={13} name="magnifier-1" />
          {searchValue.value}
        </SearchModalRow>
      ))}
    </div>
  )
}

interface SearchModalProps {
  onClose: () => void
  search: string
  setSearch: (search: string) => void
  open: boolean
}

type SearchModalState =
  | 'loading-search'
  | 'has-no-recent-searches'
  | 'show-recent-searches'
  | 'show-results'
  | 'show-no-results'

const searchSubject = new Subject<string>()

const searchObservable = searchSubject.asObservable()

const SearchModal: React.FC<SearchModalProps> = ({
  onClose,
  search,
  setSearch,
  open,
}): JSX.Element => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [recentSearches, setRecentSearches] = useState<{ value: string; timestamp: number }[]>(
    () => localStore.get('recentSearches') ?? []
  )

  const [results, setResults] = useState<InsightsConversation[]>([])
  const modalRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(modalRef, onClose)

  useEffect(() => {
    const subscription = searchObservable
      .pipe(
        debounceTime(300),
        tap((search) => {
          setRecentSearches((prevRecentSearches) => [
            {
              value: search,
              timestamp: new Date().getTime(),
            },
            ...prevRecentSearches,
          ])
        }),
        switchMap(performInsightsConversationSearch),
        catchError((_, caught) => {
          setLoading(false)
          setError(true)

          return caught
        })
      )
      .subscribe((data) => {
        setResults(data?.results)
        setLoading(false)
        setError(false)
      })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    localStore.set('recentSearches', recentSearches)
  }, [recentSearches, recentSearches.length])

  const onSearchChange = useCallback(
    (input: string) => {
      if (modalRef.current) {
        modalRef.current.scrollTop = 0
      }

      setSearch(input)
      if (input.length) {
        setLoading(true)
        searchSubject.next(input)
      }
      setHoveredIndex(null)
    },
    [setSearch, modalRef]
  )

  if (!open) {
    return <></>
  }
  const emptyResultState = 'show-no-results'

  const getModalState = () => {
    if (loading && search.length > 0) {
      return 'loading-search'
    } else if (search.length > 0 && error) {
      return emptyResultState
    } else if (search.length > 0 && !loading) {
      if (results.length > 0) {
        return 'show-results'
      } else {
        return emptyResultState
      }
    } else {
      if (recentSearches.length) {
        return 'show-recent-searches'
      }
      return 'has-no-recent-searches'
    }
  }

  const modalState: SearchModalState = getModalState()

  return (
    <Portal>
      <div className={styles.searchModalContainer}>
        <div className={'tide-base-modal--overlay'} />
        <div className={styles.searchModal} ref={modalRef}>
          <div className={styles.searchModalHeader}>
            <Icon className={cx(styles.searchIcon)} width={13} height={13} name="magnifier-1" />
            <Input
              type="text"
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
              className={styles.searchInput}
              autoFocus={true}
              placeholder={'What do you want to search for today?'}
              data-ocweb-id="SearchInput"
            />
            {search.length > 0 && (
              <div>
                <A className={styles.clearButton} type={'secondary'} onClick={() => setSearch('')}>
                  Clear
                </A>
              </div>
            )}
            <div
              className={cx(styles.closeButton, sharedStyles.customIconButton)}
              onClick={onClose}>
              <Icon width={13} height={13} name="close" />
            </div>
          </div>
          {modalState === 'has-no-recent-searches' && (
            <div className={styles.noRecentSearchesState} data-ocweb-id="HasNoRecentSearches">
              <Colorway size="large">
                <Icon name="cw-empty-search" height={80} width={80} />
              </Colorway>
              <H4>Search for any conversation you’ve had</H4>
              <P>Type in the name of the contact or a phrase that was used in the conversation.</P>
            </div>
          )}
          {modalState === 'show-recent-searches' && (
            <>
              <H6 className={styles.searchSubHeader}>RECENT SEARCHES</H6>
              <RecentSearches
                setSearch={onSearchChange}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
                searches={recentSearches}
              />
            </>
          )}
          {modalState === 'loading-search' && (
            <>
              <H6 className={styles.searchSubHeader}>SEARCH RESULTS</H6>
              <LoadingState
                size={Size.RANDOM}
                theme={Theme.LIGHT}
                count={25}
                itemClassName={styles.loadingItemStyle}
              />
            </>
          )}
          {modalState === 'show-results' && (
            <>
              <H6 className={styles.searchSubHeader} data-ocweb-id="ShowSearchResults">
                SEARCH RESULTS
              </H6>
              <ShowResults
                conversations={results}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
                closeModal={onClose}
              />
            </>
          )}
          {modalState === 'show-no-results' && (
            <div className={styles.noRecentSearchesState} data-ocweb-id="ShowNoResults">
              <Colorway size="large">
                <Icon name="cw-empty-search" height={80} width={80} />
              </Colorway>
              <H4>No results found</H4>
              <P>Check your search for typos or try using different keywords.</P>
            </div>
          )}
        </div>
      </div>
    </Portal>
  )
}

export default SearchModal
